import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FaCheckCircle, FaDesktop, FaMobileAlt, FaShoppingCart, FaSearch } from 'react-icons/fa';
import { Helmet } from 'react-helmet';
import { theme } from '../../styles/theme';

const PageContainer = styled.div`
  min-height: 100vh;
  background: ${theme.colors.primary};
  padding-top: 80px;
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 24px;
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 60px;
`;

const Title = styled.h1`
  color: ${theme.colors.secondary};
  font-size: 2.5rem;
  margin-bottom: 20px;
  
  @media screen and (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled.p`
  color: ${theme.colors.text.primary};
  font-size: 1.2rem;
  max-width: 800px;
  margin: 0 auto;
`;

const LocationHighlight = styled.span`
  color: ${theme.colors.secondary};
  font-weight: 600;
`;

const Section = styled.div`
  margin-bottom: 80px;
`;

const SectionTitle = styled.h2`
  color: ${theme.colors.secondary};
  font-size: 2rem;
  margin-bottom: 30px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 30px;
  
  @media screen and (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: ${theme.colors.primary};
  border-radius: 10px;
  padding: 30px;
  transition: all 0.3s ease;
  border: 1px solid ${theme.colors.secondary};
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
    border: 1px solid ${theme.colors.tertiary};
  }
`;

const CardIcon = styled.div`
  color: ${theme.colors.secondary};
  font-size: 2.5rem;
  margin-bottom: 20px;
`;

const CardTitle = styled.h3`
  color: ${theme.colors.secondary};
  font-size: 1.5rem;
  margin-bottom: 15px;
`;

const CardText = styled.p`
  color: ${theme.colors.text.primary};
  font-size: 1rem;
  line-height: 1.6;
`;

const FeaturesList = styled.div`
  margin-top: 20px;
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 15px;
`;

const FeatureIcon = styled.div`
  color: ${theme.colors.secondary};
  font-size: 1.2rem;
  margin-right: 10px;
  margin-top: 3px;
`;

const FeatureText = styled.p`
  font-size: 1rem;
  line-height: 1.5;
  color: ${theme.colors.text.primary};
`;

const ProcessSection = styled.div`
  margin-bottom: 80px;
`;

const ProcessSteps = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
`;

const ProcessStep = styled.div`
  display: flex;
  gap: 20px;
  
  @media screen and (max-width: 768px) {
    flex-direction: column;
  }
`;

const StepNumber = styled.div`
  background: ${theme.colors.secondary};
  color: ${theme.colors.text.secondary};
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: bold;
  flex-shrink: 0;
`;

const StepContent = styled.div`
  flex: 1;
`;

const StepTitle = styled.h3`
  color: #fff;
  font-size: 1.5rem;
  margin-bottom: 10px;
`;

const StepDescription = styled.p`
  color: #fff;
  font-size: 1rem;
  line-height: 1.6;
`;

const CTASection = styled.div`
  background: linear-gradient(135deg, #01bf71, #018f55);
  border-radius: 10px;
  padding: 60px;
  text-align: center;
  
  @media screen and (max-width: 768px) {
    padding: 40px 20px;
  }
`;

const CTATitle = styled.h2`
  color: #fff;
  font-size: 2.5rem;
  margin-bottom: 20px;
  
  @media screen and (max-width: 768px) {
    font-size: 2rem;
  }
`;

const CTAText = styled.p`
  color: #fff;
  font-size: 1.2rem;
  margin-bottom: 30px;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
`;

const CTAButton = styled(Link)`
  display: inline-block;
  background: transparent;
  border: 2px solid ${theme.colors.secondary};
  color: ${theme.colors.secondary};
  font-size: 1.2rem;
  font-weight: 600;
  padding: 15px 30px;
  border-radius: 50px;
  text-decoration: none;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${theme.colors.primary};
    color: ${theme.colors.text.primary};
    transform: translateY(-3px);
  }
`;

const WebsiteCreationPretoria = () => {
  return (
    <PageContainer>
      <Helmet>
        <title>Website Creation in Pretoria | Professional Web Design Services | Afrotech Group</title>
        <meta name="description" content="Professional website creation services in Pretoria, South Africa. Custom web design, e-commerce solutions, and responsive websites for local businesses. Contact Afrotech Group today!" />
        <meta name="keywords" content="website creation Pretoria, web design Pretoria, professional website Pretoria, business website South Africa, Afrotech Group web design" />
      </Helmet>
      
      <ContentWrapper>
        <PageHeader>
          <Title>Professional Website Creation in <LocationHighlight>Pretoria</LocationHighlight></Title>
          <Subtitle>
            Elevate your business with custom, responsive, and SEO-optimized websites designed specifically for the Pretoria market
          </Subtitle>
        </PageHeader>
        
        <Section>
          <SectionTitle>Our Website Creation Services in Pretoria</SectionTitle>
          <Grid>
            <Card>
              <CardIcon><FaDesktop /></CardIcon>
              <CardTitle>Business Websites</CardTitle>
              <CardText>
                Professional websites that showcase your Pretoria business with modern design, clear messaging, and seamless functionality.
              </CardText>
              <FeaturesList>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>Mobile-responsive design</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>Local SEO optimization</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>Contact forms & maps integration</FeatureText>
                </FeatureItem>
              </FeaturesList>
            </Card>
            
            <Card>
              <CardIcon><FaShoppingCart /></CardIcon>
              <CardTitle>E-Commerce Solutions</CardTitle>
              <CardText>
                Custom online stores that help Pretoria businesses sell products and services with secure payment processing.
              </CardText>
              <FeaturesList>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>South African payment gateways</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>Inventory management</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>Order tracking & notifications</FeatureText>
                </FeatureItem>
              </FeaturesList>
            </Card>
            
            <Card>
              <CardIcon><FaMobileAlt /></CardIcon>
              <CardTitle>Responsive Web Design</CardTitle>
              <CardText>
                Websites that look and function perfectly on all devices, ensuring your Pretoria customers have a seamless experience.
              </CardText>
              <FeaturesList>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>Mobile-first approach</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>Fast loading speeds</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>Touch-friendly navigation</FeatureText>
                </FeatureItem>
              </FeaturesList>
            </Card>
            
            <Card>
              <CardIcon><FaSearch /></CardIcon>
              <CardTitle>Local SEO Optimization</CardTitle>
              <CardText>
                Boost your visibility in Pretoria with websites optimized for local search, helping customers find your business.
              </CardText>
              <FeaturesList>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>Pretoria-focused keywords</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>Google My Business integration</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><FaCheckCircle /></FeatureIcon>
                  <FeatureText>Local business schema markup</FeatureText>
                </FeatureItem>
              </FeaturesList>
            </Card>
          </Grid>
        </Section>
        
        <ProcessSection>
          <SectionTitle>Our Website Creation Process</SectionTitle>
          <ProcessSteps>
            <ProcessStep>
              <StepNumber>1</StepNumber>
              <StepContent>
                <StepTitle>Discovery & Planning</StepTitle>
                <StepDescription>
                  We meet with you in Pretoria to understand your business goals, target audience, and website requirements. Our team develops a comprehensive plan tailored to your specific needs.
                </StepDescription>
              </StepContent>
            </ProcessStep>
            
            <ProcessStep>
              <StepNumber>2</StepNumber>
              <StepContent>
                <StepTitle>Design & Wireframing</StepTitle>
                <StepDescription>
                  Our designers create mockups and wireframes that align with your brand identity and appeal to your Pretoria audience, ensuring an intuitive user experience.
                </StepDescription>
              </StepContent>
            </ProcessStep>
            
            <ProcessStep>
              <StepNumber>3</StepNumber>
              <StepContent>
                <StepTitle>Development & Content Integration</StepTitle>
                <StepDescription>
                  Our development team builds your website using modern technologies, ensuring it's fast, secure, and responsive. We integrate your content with a focus on local Pretoria relevance.
                </StepDescription>
              </StepContent>
            </ProcessStep>
            
            <ProcessStep>
              <StepNumber>4</StepNumber>
              <StepContent>
                <StepTitle>Testing & Launch</StepTitle>
                <StepDescription>
                  We thoroughly test your website across all devices and browsers before launching it. Once approved, we deploy your site and ensure it's properly indexed by search engines.
                </StepDescription>
              </StepContent>
            </ProcessStep>
            
            <ProcessStep>
              <StepNumber>5</StepNumber>
              <StepContent>
                <StepTitle>Support & Maintenance</StepTitle>
                <StepDescription>
                  Our Pretoria-based team provides ongoing support and maintenance to keep your website secure, up-to-date, and performing optimally for your business.
                </StepDescription>
              </StepContent>
            </ProcessStep>
          </ProcessSteps>
        </ProcessSection>
        
        <CTASection>
          <CTATitle>Ready to Create Your Professional Website?</CTATitle>
          <CTAText>
            Contact our Pretoria web design team today for a free consultation and quote. Let's build a website that helps your business stand out in the local market.
          </CTAText>
          <CTAButton to="/contact">Get a Free Quote</CTAButton>
        </CTASection>
      </ContentWrapper>
    </PageContainer>
  );
};

export default WebsiteCreationPretoria;
